import { useEffect, useRef } from 'react';
import { PlanCard } from './PlanCard';
import type { PricingPlanKey } from './pricingContent';
import type { PlanView } from './PricingCatalog';

/**
 * Carrusel horizontal de las cards de planes — **sólo mobile**
 * (ADR-2026-08-03-cards-de-planes-deslizables-en-mobile).
 *
 * Antes mobile mostraba UNA card y se cambiaba de plan por el segmentado de arriba.
 * Ahora las tres están montadas y se deslizan con el dedo; el segmentado queda como
 * atajo y como indicador de dónde estás parado.
 *
 * Va con **scroll-snap nativo** y no con una librería de gestos: el navegador ya
 * resuelve el arrastre, la inercia, el rebote y el snap con el comportamiento propio
 * de cada plataforma, y encima queda accesible por teclado y por scroll horizontal en
 * desktop sin escribir nada.
 *
 * El plan "en foco" y `selected` son lo mismo: el CTA fijo del pie cobra el plan que
 * se está mirando, así que deslizar TIENE que actualizar la selección. La
 * sincronización va en los dos sentidos y por eso lleva DOS guards: `scrollPropio`
 * (deslizar → `onSelect` → efecto → `scrollTo` se pisaría a sí mismo a mitad del
 * gesto) y `destinoProgramatico` (el `scrollTo` smooth del segmentado pasa por
 * encima de las cards intermedias y sus eventos de scroll las reportaban como
 * seleccionadas → la pastilla iba y volvía; ver
 * ADR-2026-08-24-carrusel-planes-ignora-scroll-programatico).
 */
export interface PlanCarouselProps {
  plans: PlanView[];
  selected: PricingPlanKey;
  onSelect: (key: PricingPlanKey) => void;
  /** Slider de tiers — sólo lo recibe la card de Ultra. */
  ultraTier?: {
    index: number;
    count: number;
    onChange: (index: number) => void;
  };
}

export function PlanCarousel({ plans, selected, onSelect, ultraTier }: PlanCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  /** El último cambio de `selected` lo originó un deslizamiento de este carrusel. */
  const scrollPropio = useRef(false);
  /**
   * `scrollLeft` al que viaja un `scrollTo` smooth disparado desde afuera (tap en
   * el segmentado). Mientras está en vuelo, los eventos de scroll NO eligen plan:
   * la animación pasa por encima de las cards intermedias y `alDeslizar` las
   * reportaba como seleccionadas, con lo que la pastilla del segmentado y el CTA
   * del pie iban y volvían en cada tap. Se limpia al llegar al destino, o si el
   * usuario agarra el carrusel (lo que además corta la animación nativa).
   */
  const destinoProgramatico = useRef<number | null>(null);

  /**
   * `scrollLeft` que centra la card, acotado al rango scrolleable REAL: la última
   * card no puede quedar centrada (el browser clampea el `scrollTo`), y sin este
   * clamp la detección de llegada esperaría un destino inalcanzable.
   */
  const destinoDe = (el: HTMLElement, card: HTMLElement) =>
    Math.max(
      0,
      Math.min(
        card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2,
        el.scrollWidth - el.clientWidth,
      ),
    );

  /** Centra la card `i` dentro del scroller. */
  const centrar = (i: number, behavior: ScrollBehavior) => {
    const el = scrollerRef.current;
    const card = el?.children[i] as HTMLElement | undefined;
    if (!el || !card) return;
    const left = destinoDe(el, card);
    destinoProgramatico.current = behavior === 'smooth' ? left : null;
    el.scrollTo({ left, behavior });
  };

  // Selección desde AFUERA (el segmentado, o el plan recomendado inicial) → traer esa
  // card a la vista. Si el cambio vino de un deslizamiento, no se toca el scroll: ya
  // está donde el dedo lo dejó.
  useEffect(() => {
    if (scrollPropio.current) {
      scrollPropio.current = false;
      return;
    }
    const i = plans.findIndex((p) => p.key === selected);
    if (i >= 0) centrar(i, 'smooth');
    // `plans` cambia de identidad en cada render del padre (se arma con
    // `buildPlanViews`), así que la dependencia es la CLAVE, no el array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Posición inicial sin animación: si el plan recomendado no es el primero, la card
  // tiene que aparecer ya centrada y no viajando desde el borde.
  useEffect(() => {
    const i = plans.findIndex((p) => p.key === selected);
    if (i > 0) centrar(i, 'auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Card cuyo centro quedó más cerca del centro del scroller. */
  const alDeslizar = () => {
    const el = scrollerRef.current;
    if (!el) return;
    // Viaje programático en vuelo: no elegir plan hasta llegar. La tolerancia
    // absorbe el subpíxel con que puede reposar el scroll.
    if (destinoProgramatico.current !== null) {
      if (Math.abs(el.scrollLeft - destinoProgramatico.current) <= 2)
        destinoProgramatico.current = null;
      return;
    }
    const centro = el.scrollLeft + el.clientWidth / 2;
    let mejor = 0;
    let mejorDist = Infinity;
    Array.from(el.children).forEach((child, i) => {
      const c = child as HTMLElement;
      const dist = Math.abs(c.offsetLeft + c.offsetWidth / 2 - centro);
      if (dist < mejorDist) {
        mejorDist = dist;
        mejor = i;
      }
    });
    const key = plans[mejor]?.key;
    if (key && key !== selected) {
      scrollPropio.current = true;
      onSelect(key);
    }
  };

  /** El usuario agarró el carrusel: si había un viaje programático, manda él. */
  const cortarViaje = () => {
    destinoProgramatico.current = null;
  };

  return (
    <div
      ref={scrollerRef}
      onScroll={alDeslizar}
      // El dedo (o la rueda, en el breakpoint intermedio con mouse) interrumpe el
      // viaje del segmentado: sin esto, el guard seguiría armado y el primer
      // deslizamiento manual no actualizaría la selección.
      onPointerDown={cortarViaje}
      onWheel={cortarViaje}
      // `-mx-5 px-5`: las cards sangran hasta los bordes de la pantalla (se ve el
      // asomo de la siguiente) pero la primera arranca alineada con el resto del
      // contenido. `pb-2` deja aire para la sombra al hacer snap.
      // `pricing-scroller-hidden` esconde la barra de scroll (ver index.css): en
      // mobile no aporta y en desktop cortaba la card.
      className="pricing-scroller-hidden -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2"
    >
      {plans.map((p) => (
        // `w-[90%]`: el ancho lo acota el ASOMO de la card vecina, no la card en sí.
        // Ese asomo es lo único que dice "esto se desliza" —no hay flechas ni puntos—,
        // así que puede crecer mientras siga leyéndose. A 90% del ancho útil quedan
        // ~40px de la siguiente a la vista en un teléfono de 393px, que alcanza; más
        // que eso empieza a parecer una card suelta. Ver ADR-2026-08-03-cards-de-planes-deslizables-en-mobile.
        <div key={p.key} className="w-[90%] shrink-0 snap-center">
          <PlanCard
            className="h-full"
            content={p.content}
            credits={p.credits}
            generates={p.generates}
            price={p.price}
            period={p.period}
            strikePrice={p.strikePrice}
            discountLabel={p.discountLabel}
            ivaIncluido={p.ivaIncluido}
            isCurrent={p.isCurrent}
            ultraTier={p.key === 'enterprise' ? ultraTier : undefined}
          />
        </div>
      ))}
    </div>
  );
}

export default PlanCarousel;
